import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import { resolvePhotoUrl } from '../../utils/photoUrl';
import { TaxonDiagnosticPanel } from '../../components/TaxonDiagnostics';
import usePanZoom from '../../hooks/usePanZoom';

/**
 * Mushroom Spotter -- find the mushroom on a field photograph, against the clock.
 *
 * Each round: a photograph and a countdown. Drag a box around the mushroom
 * (the ellipse drawn inside it is the "circle"), or tap on it. Letting go
 * sends the answer; the server (routes/spotter.py) scores it against where
 * the Tagging game's annotators painted the mushroom and only then reveals
 * that place, with what the species looks like and where it grows. Give up
 * reveals it too, for no points. Hits in a row build a streak bonus.
 *
 * The ground truth never reaches the browser before the answer, so the game
 * cannot be won by reading the network tab. Best sessions are kept on this
 * device (a local leaderboard) as well as on the server's 30-day board.
 */
export const ROUND_SECONDS = 30;
export const ROUNDS = 10;
export const TIPS = 8;
const LOCAL_KEY = 'pitriot.spotter.best';
const TAP = 0.03;

export function readLocalBoard() {
  try {
    const raw = JSON.parse(localStorage.getItem(LOCAL_KEY) || '[]');
    return Array.isArray(raw) ? raw : [];
  } catch {
    return [];
  }
}

export function addToLocalBoard(entry, limit = 10) {
  const board = [...readLocalBoard(), entry].sort((a, b) => b.points - a.points).slice(0, limit);
  try { localStorage.setItem(LOCAL_KEY, JSON.stringify(board)); } catch { /* private mode: the board is just not kept */ }
  return board;
}

/** A drag from `a` to `b` (fractions), as {x, y, w, h}; a short drag is a tap. */
export function boxFrom(a, b) {
  const x = Math.min(a.x, b.x);
  const y = Math.min(a.y, b.y);
  const w = Math.abs(a.x - b.x);
  const h = Math.abs(a.y - b.y);
  if (w < TAP && h < TAP) return { x: Math.max(0, a.x - 0.005), y: Math.max(0, a.y - 0.005), w: 0.01, h: 0.01 };
  return { x, y, w: Math.min(w, 1 - x), h: Math.min(h, 1 - y) };
}

function Overlay({ box, truth, drawing }) {
  const shape = (b, style, key) => (
    <g key={key}>
      <rect x={`${b.x * 100}%`} y={`${b.y * 100}%`} width={`${b.w * 100}%`} height={`${b.h * 100}%`}
            style={{ fill: 'none', ...style, strokeDasharray: '4 3' }} />
      <ellipse cx={`${(b.x + b.w / 2) * 100}%`} cy={`${(b.y + b.h / 2) * 100}%`}
               rx={`${(b.w / 2) * 100}%`} ry={`${(b.h / 2) * 100}%`} style={{ fill: 'none', ...style }} />
    </g>
  );
  return (
    <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}>
      {(truth || []).map((t, i) => shape(t, { stroke: 'var(--c-success)', strokeWidth: 3 }, `t${i}`))}
      {box && shape(box, { stroke: drawing ? 'var(--c-warning)' : 'var(--c-info)', strokeWidth: 3 }, 'mine')}
    </svg>
  );
}

export default function SpotterGame() {
  const { t } = useTranslation('game');
  // Callbacks read t through a ref: a new t identity must not refetch.
  const tRef = useRef(t);
  tRef.current = t;
  const [filters, setFilters] = useState({ country: '', source: '' });
  const [countries, setCountries] = useState([]);
  const [sources, setSources] = useState([]);
  const [queue, setQueue] = useState([]);
  const [round, setRound] = useState(0);
  const [phase, setPhase] = useState('ready'); // ready | playing | result | done | empty
  const [left, setLeft] = useState(ROUND_SECONDS);
  const [start, setStart] = useState(null);
  const [drag, setDrag] = useState(null);
  const [mine, setMine] = useState(null);
  const [result, setResult] = useState(null);
  const [session, setSession] = useState({ points: 0, hits: 0, bestStreak: 0 });
  const [board, setBoard] = useState(readLocalBoard);
  const [server, setServer] = useState(null);
  const [error, setError] = useState('');
  const startedAt = useRef(0);
  const sending = useRef(false);
  const frame = useRef(null);
  // The finger drawing the box: only its own lift sends the answer.
  const drawer = useRef(null);

  const loadQueue = useCallback(async () => {
    setError('');
    try {
      const { data } = await api.get('/spotter/queue', { params: {
        limit: ROUNDS, ...(filters.country ? { country: filters.country } : {}),
        ...(filters.source ? { source: filters.source } : {}) } });
      setCountries(data.countries || []);
      setSources(data.sources || []);
      return data.photos || [];
    } catch (e) {
      setError(e?.response?.data?.error || tRef.current('spotter.loadFailed'));
      return null;
    }
  }, [filters]);

  useEffect(() => { loadQueue(); }, [loadQueue]);

  const play = async () => {
    const photos = await loadQueue();
    if (!photos) return;
    if (!photos.length) { setPhase('empty'); return; }
    setQueue(photos);
    setRound(0);
    setSession({ points: 0, hits: 0, bestStreak: 0 });
    beginRound();
  };

  const beginRound = () => {
    setMine(null); setDrag(null); setStart(null); setResult(null);
    setLeft(ROUND_SECONDS);
    startedAt.current = Date.now();
    sending.current = false;
    setPhase('playing');
  };

  const send = useCallback(async (box) => {
    if (sending.current) return;
    sending.current = true;
    const photo = queue[round];
    try {
      const { data } = await api.post('/spotter/attempts', {
        photo_id: photo.photo_id, duration_ms: Date.now() - startedAt.current,
        ...(box ? { box } : { gave_up: true }) });
      setResult(data);
      setSession((s) => ({ points: s.points + data.points, hits: s.hits + (data.hit ? 1 : 0),
                           bestStreak: Math.max(s.bestStreak, data.streak) }));
      setPhase('result');
    } catch (e) {
      sending.current = false;
      setError(e?.response?.data?.error || tRef.current('spotter.sendFailed'));
    }
  }, [queue, round]);

  // The countdown: at zero the round is given up.
  useEffect(() => {
    if (phase !== 'playing') return undefined;
    const id = setInterval(() => {
      const remaining = ROUND_SECONDS - Math.floor((Date.now() - startedAt.current) / 1000);
      setLeft(Math.max(0, remaining));
      if (remaining <= 0) send(null);
    }, 250);
    return () => clearInterval(id);
  }, [phase, send]);

  const at = (e) => {
    const r = frame.current.getBoundingClientRect();
    return { x: Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)),
             y: Math.min(1, Math.max(0, (e.clientY - r.top) / r.height)) };
  };
  /* Two fingers pinch the photograph and move it (2026-10-15): the box the
   * first finger began is dropped, and nothing is sent until one finger draws
   * and lifts. The box maps through the magnified frame, so it lands where it
   * is drawn. */
  const pz = usePanZoom({
    resetKey: `${round}:${queue[round]?.photo_id || ''}`,
    singlePointer: 'ignore', wheel: 'ctrl', doubleTap: false, keyboard: false,
    onGestureStart: () => { drawer.current = null; setStart(null); setDrag(null); },
  });
  const down = (e) => {
    if (phase !== 'playing' || drawer.current !== null) return;
    drawer.current = e.pointerId ?? 'mouse';
    try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch (_) { /* jsdom, or not capturable */ }
    const p = at(e);
    setStart(p);
    setDrag(boxFrom(p, p));
  };
  const move = (e) => { if (start && drawer.current === (e.pointerId ?? 'mouse')) setDrag(boxFrom(start, at(e))); };
  const up = (e) => {
    const mineFinger = drawer.current === (e.pointerId ?? 'mouse');
    if (mineFinger) drawer.current = null;
    if (!start || !mineFinger) return;
    const box = boxFrom(start, at(e));
    setStart(null); setDrag(null); setMine(box);
    send(box);
  };
  const cancel = () => { drawer.current = null; setStart(null); setDrag(null); };

  const next = async () => {
    if (round + 1 < queue.length) { setRound(round + 1); beginRound(); return; }
    setBoard(addToLocalBoard({ points: session.points, hits: session.hits, rounds: queue.length,
                               bestStreak: session.bestStreak, at: new Date().toISOString().slice(0, 10) }));
    setPhase('done');
    api.get('/spotter/leaderboard').then(({ data }) => setServer(data)).catch(() => setServer(null));
  };

  const photo = queue[round];
  return (
    <section data-testid="spotter" style={{ display: 'grid', gap: 12 }}>
      <p style={{ margin: 0, color: 'var(--c-text-muted-fg)' }}>{t('spotter.intro')}</p>
      {error && <p role="alert" data-testid="spotter-error">{error}</p>}

      {(phase === 'ready' || phase === 'empty' || phase === 'done') && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label>{t('spotter.country')}
            <select value={filters.country} data-testid="spotter-country" title={t('spotter.tip.country')}
                    onChange={(e) => setFilters((f) => ({ ...f, country: e.target.value }))}>
              <option value="">{t('spotter.anyCountry')}</option>
              {countries.map((c) => <option key={c.code} value={c.code}>{c.code} ({c.photos})</option>)}
            </select>
          </label>
          <label>{t('spotter.source')}
            <select value={filters.source} data-testid="spotter-source" title={t('spotter.tip.source')}
                    onChange={(e) => setFilters((f) => ({ ...f, source: e.target.value }))}>
              <option value="">{t('spotter.anySource')}</option>
              {sources.map((s) => <option key={s} value={s}>{t(`spotter.sources.${s}`, { defaultValue: s })}</option>)}
            </select>
          </label>
          <button type="button" onClick={play} data-testid="spotter-play" title={t('spotter.tip.play')}>
            {phase === 'done' ? t('spotter.playAgain') : t('spotter.play')}
          </button>
        </div>
      )}
      {phase === 'empty' && <p data-testid="spotter-empty">{t('spotter.empty')}</p>}

      {(phase === 'playing' || phase === 'result') && photo && (
        <>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontWeight: 600 }} data-testid="spotter-hud">
            <span>{t('spotter.round', { n: round + 1, total: queue.length })}</span>
            <span data-testid="spotter-timer" style={{ color: left <= 5 ? 'var(--c-danger-fg)' : undefined }}>
              ⏱ {left}s</span>
            <span data-testid="spotter-score">{t('spotter.score', { points: session.points })}</span>
            {result?.streak > 1 && <span>🔥 {t('spotter.streak', { n: result.streak })}</span>}
          </div>
          <div {...pz.frameProps} data-testid="spotter-viewport"
               data-scale={String(Math.round(pz.view.scale * 1000) / 1000)}
               style={{ ...pz.frameProps.style, touchAction: 'none', position: 'relative', maxWidth: 820,
                        overflow: 'hidden', borderRadius: 8 }}>
            <div ref={frame} data-testid="spotter-frame" title={t('spotter.tip.frame')}
                 onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={cancel}
                 style={{ position: 'relative', touchAction: 'none', userSelect: 'none', ...pz.contentStyle,
                          cursor: phase === 'playing' ? 'crosshair' : 'default' }}>
              <img src={resolvePhotoUrl(photo.image_url)} alt={t('spotter.photoAlt')} draggable={false}
                   style={{ width: '100%', display: 'block', borderRadius: 8 }} />
              <Overlay box={drag || mine} drawing={Boolean(drag)} truth={phase === 'result' ? result?.truth : null} />
            </div>
          </div>
          {phase === 'playing' && (
            <button type="button" onClick={() => send(null)} data-testid="spotter-giveup" title={t('spotter.tip.giveUp')}>
              {t('spotter.giveUp')}
            </button>
          )}
          {phase === 'result' && result && (
            <div data-testid="spotter-result" role="status" style={{ display: 'grid', gap: 8 }}>
              <strong>{result.hit ? t('spotter.hit', { points: result.points })
                : (mine ? t('spotter.miss') : t('spotter.revealed'))}</strong>
              {result.iou != null && <span>{t('spotter.overlap', { pct: Math.round(result.iou * 100) })}</span>}
              <p style={{ margin: 0 }} data-testid="spotter-tip">💡 {t(`spotter.tips.${round % TIPS}`)}</p>
              {result.taxon && <TaxonDiagnosticPanel name={result.taxon} heading={t('spotter.aboutTaxon', { taxon: result.taxon })}
                                                     testId="spotter-taxon" />}
              <button type="button" onClick={next} data-testid="spotter-next" title={t('spotter.tip.next')}>
                {round + 1 < queue.length ? t('spotter.next') : t('spotter.finish')}
              </button>
            </div>
          )}
        </>
      )}

      {phase === 'done' && (
        <div data-testid="spotter-done" style={{ display: 'grid', gap: 8 }}>
          <h3 style={{ margin: 0 }}>{t('spotter.sessionScore', { points: session.points })}</h3>
          <p style={{ margin: 0 }}>{t('spotter.sessionHits', { hits: session.hits, rounds: queue.length, streak: session.bestStreak })}</p>
          <h4 style={{ margin: '8px 0 0' }}>{t('spotter.localBoard')}</h4>
          <ol data-testid="spotter-local-board" style={{ margin: 0 }}>
            {board.map((b, i) => <li key={`${b.at}-${i}`}>{b.points} · {b.hits}/{b.rounds} · {b.at}</li>)}
          </ol>
          {server && (
            <>
              <h4 style={{ margin: '8px 0 0' }}>{t('spotter.serverBoard', { days: server.days })}</h4>
              <ol data-testid="spotter-server-board" style={{ margin: 0 }}>
                {server.players.map((p) => (
                  <li key={p.user_id} style={{ fontWeight: p.is_me ? 700 : 400 }}>
                    {p.username} · {p.points} · 🔥 {p.best_streak}
                  </li>
                ))}
              </ol>
            </>
          )}
        </div>
      )}
    </section>
  );
}
